import {useEffect, useMemo, useState, type ReactNode} from "react";
import {User} from "../../../api/accessControl/User.ts";
import {DrillDownDrawer, type DrillDownLine} from "../DrillDown/index.ts";
import {toCentsFromMoney} from "../../../lib/Money.ts";
import type {LegalEntityType} from "../../../generated/enums.ts";
import {
    KpiFormulaPopover,
    AccountWarningsNotice,
    PeriodicToolbar,
    PeriodicStatementTable,
} from "./components";
import {
    ReportRowKind,
    type KpiPopoverState,
    type PeriodicProfitAndLossReport,
    type PeriodicRow,
    type PeriodicViewRow,
} from "./types";
import {
    clampYtdMonthIndex,
    formatSignedCents,
    getStoredPrefs,
    hasWarnedRows,
    kpiFormulaText,
    linesForPeriod,
    monthColumnsForBuckets,
    rowLabel,
    storePrefs,
} from "./utils";
import {
    usePeriodicStatement,
    type FetchPeriodicStatement,
    type PeriodicStatementCopy,
} from "./usePeriodicStatement.ts";

const PROFIT_AND_LOSS_COPY: PeriodicStatementCopy = {
    loadFailedTitle: "Kon W&V per maand niet laden",
    refreshedTitle: "W&V per maand vernieuwd",
    refreshFailedDescription: "Kon de W&V per maand niet vernieuwen. Probeer het opnieuw.",
    logName: "monthly profit and loss",
};

export interface PeriodicPnlViewProps {
    currentUser: User;
    businessId: string;
    legalEntityType: LegalEntityType;
    fetchProfitAndLossPerPeriod: FetchPeriodicStatement<PeriodicProfitAndLossReport>;
    refreshTrialBalanceCache: () => Promise<{invalidated_count: number}>;
    toolbarLeading?: ReactNode;
}

export default function PeriodicPnlView({
    currentUser,
    businessId,
    legalEntityType,
    fetchProfitAndLossPerPeriod,
    refreshTrialBalanceCache,
    toolbarLeading,
}: PeriodicPnlViewProps) {
    const userId = currentUser.id;
    const statement = usePeriodicStatement<PeriodicRow, PeriodicProfitAndLossReport>({
        userId,
        businessId,
        legalEntityType,
        fetchReport: fetchProfitAndLossPerPeriod,
        refreshTrialBalanceCache,
        copy: PROFIT_AND_LOSS_COPY,
    });
    const {reportData, rows, visibleRows, isLoading, searchQuery} = statement;

    const [ytdOverride, setYtdOverride] = useState<number | null>(
        () => getStoredPrefs(userId, businessId).periodicYtdMonthIndex,
    );
    const [kpiPopover, setKpiPopover] = useState<KpiPopoverState | null>(null);
    const [isDrillDownOpen, setIsDrillDownOpen] = useState(false);
    const [selectedLines, setSelectedLines] = useState<DrillDownLine[]>([]);
    const [selectedRowTitle, setSelectedRowTitle] = useState("");

    useEffect(() => {
        setYtdOverride(getStoredPrefs(userId, businessId).periodicYtdMonthIndex);
    }, [businessId, userId]);

    const monthColumns = useMemo(
        () => (reportData ? monthColumnsForBuckets(reportData.buckets) : []),
        [reportData],
    );

    const ytdMonthIndex = useMemo(
        () => clampYtdMonthIndex(ytdOverride ?? monthColumns.length - 1, monthColumns.length),
        [ytdOverride, monthColumns.length],
    );

    const hasActivity = rows.some(
        (row) =>
            row.kind === ReportRowKind.Account &&
            (toCentsFromMoney(row.total_signed_net) !== 0 ||
                row.cells.some((cell) => toCentsFromMoney(cell.signed_net) !== 0)),
    );

    function handleYtdChange(index: number) {
        setYtdOverride(index);
        storePrefs(userId, businessId, {
            ...getStoredPrefs(userId, businessId),
            periodicYtdMonthIndex: index,
        });
    }

    function openDrawer(row: PeriodicViewRow, periodIndex: number | null) {
        const code = row.account?.code;
        const label = rowLabel(row, legalEntityType);
        const monthSuffix =
            periodIndex !== null && monthColumns[periodIndex]
                ? ` — ${monthColumns[periodIndex].label}`
                : "";
        setSelectedLines(linesForPeriod(row.lines, periodIndex));
        setSelectedRowTitle(`${code ? `${code} - ` : ""}${label}${monthSuffix}`);
        setIsDrillDownOpen(true);
    }

    function handleKpiClick(row: PeriodicViewRow) {
        if (!row.label_key) return;
        const ytdCell = row.cells[ytdMonthIndex];
        const popoverCents = toCentsFromMoney(
            ytdCell?.cumulative_signed_net ?? row.total_signed_net,
        );
        setKpiPopover({
            label: rowLabel(row, legalEntityType),
            formula: kpiFormulaText(row.label_key, legalEntityType),
            value: formatSignedCents(popoverCents),
        });
    }

    const ytdDropdownOptions = monthColumns.map((column, index) => ({
        value: String(index),
        label: column.label,
    }));

    const searchNoAccounts =
        !isLoading &&
        !!searchQuery.trim() &&
        hasActivity &&
        !visibleRows.some((row) => row.account);

    return (
        <>
            <PeriodicToolbar
                dateFrom={statement.dateFrom}
                dateTo={statement.dateTo}
                onDateRangeChange={statement.handleDateRangeChange}
                leading={toolbarLeading}
                ytd={{
                    monthIndex: ytdMonthIndex,
                    options: ytdDropdownOptions,
                    onChange: handleYtdChange,
                }}
                selectedCurrency={statement.selectedCurrency}
                currencyOptions={statement.currencyOptions}
                onCurrencyChange={statement.setSelectedCurrency}
                showPredictedAccounts={statement.showPredictedAccounts}
                onPredictedChange={statement.handlePredictedChange}
                searchQuery={searchQuery}
                onSearchChange={statement.setSearchQuery}
                onRefresh={() => void statement.refresh()}
                isRefreshingCache={statement.isRefreshingCache}
                isLoading={isLoading}
                beyondDefault={statement.beyondDefault}
                onToggleAll={statement.handleToggleAll}
            />

            <AccountWarningsNotice visible={hasWarnedRows(rows)} />

            <div className="relative min-h-[12rem] w-full">
                <div className="my-4">
                    <PeriodicStatementTable
                        loading={isLoading}
                        skeletonMonthCount={statement.skeletonMonthCount}
                        rows={hasActivity && !searchNoAccounts ? visibleRows : []}
                        monthColumns={monthColumns}
                        ytdMonthIndex={ytdMonthIndex}
                        currency={statement.selectedCurrency}
                        legalEntityType={legalEntityType}
                        onToggleNode={statement.handleToggleNode}
                        onAccountClick={(row) => openDrawer(row, null)}
                        onCellClick={(row, periodIndex) => openDrawer(row, periodIndex)}
                        onKpiClick={handleKpiClick}
                        emptyMessage={
                            searchNoAccounts
                                ? `Geen rekeningen gevonden voor “${searchQuery.trim()}”.`
                                : undefined
                        }
                    />
                </div>
            </div>

            <DrillDownDrawer
                open={isDrillDownOpen}
                onClose={() => setIsDrillDownOpen(false)}
                title={selectedRowTitle}
                businessId={businessId}
                rows={selectedLines}
                headingPrefix="Bronnen"
                emptyMessage="Er zijn geen facturen of transacties gevonden voor deze rekening."
            />

            {kpiPopover && (
                <KpiFormulaPopover
                    label={kpiPopover.label}
                    formula={kpiPopover.formula}
                    value={kpiPopover.value}
                    onClose={() => setKpiPopover(null)}
                />
            )}
        </>
    );
}
